"use client";

/**
 * Builder context to replace prop drilling of event/page metadata
 * Provider wraps at BuilderLayout, consumed by LeftSidebar, Canvas, BuilderHeader
 */

import { createContext, useContext } from "react";
import type { EventDataForAI } from "../_logic/types/template-types";
import type { LandingPageAccess } from "../_logic/types/db-types";

/** Read-only metadata shared across the builder */
export interface BuilderContextValue {
  /** Event ID for server actions */
  eventId: number;
  /** Main (parent) page slug — used for navigation and preview links */
  parentSlug: string;
  /**
   * Top-level (root) page slug, even when rendering a subpage. Use for links that must target the
   * top-level page (e.g. the focused schedule `?view=schedule`), since `parentSlug` is the current
   * page's own slug in the public view (i.e. the subpage slug on a subpage).
   */
  rootSlug: string;
  /** Main (parent) page ID — used for listing/creating subpages */
  parentPageId: number;
  /** Slug of the subpage being edited, or null for main page */
  currentSubpageSlug: string | null;
  /** Primary brand color for the landing page (hex, e.g. "#5261EB") */
  primaryColor: string;
  /** Accent/highlight brand color (hex). Opt-in palette color used for highlights (e.g. countdown digits). */
  accentColor: string;
  /** Page/section background color (hex) for palette-aware sections. */
  backgroundColor: string;
  /** Body/text color (hex) for palette-aware sections. */
  textColor: string;
  /** Event data subset for AI template generation */
  eventData: EventDataForAI | null;
  /** Page-wide IANA timezone (hero dates, activity slots, schedule, .ics). NULL = floating time. */
  timeZone: string | null;
  /** Access gate for the landing page: "public" allows anyone, "invited"/"private" require login. */
  accessLevel: LandingPageAccess;
}

export const BuilderContext = createContext<BuilderContextValue | null>(null);

/**
 * Access builder-level metadata (eventId, parentSlug, parentPageId, currentSubpageSlug)
 * Must be used within a BuilderContext.Provider
 * @returns The builder context value
 * @throws If used outside of a BuilderContext provider
 */
export function useBuilderContext(): BuilderContextValue {
  const ctx = useContext(BuilderContext);
  if (!ctx) {
    throw new Error("useBuilderContext must be used within a BuilderContext.Provider");
  }
  return ctx;
}
